import React, { useMemo, useContext, useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import HTMLRenderer from 'react-html-renderer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import Image from 'components/Common/Image'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ModalContext from 'contexts/ModalContext'
import ShipFacilitiesModal from './ShipFacilitiesModal'

const ShipImage = styled(Image)`
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.S};

  ${mediaQueryUp.desktop} {
    width: ${rem(448)};
  }
`

const FactText = styled(BodyText)`
  display: flex;
  gap: ${rem(4)};
`

interface Props { offer: App.CruiseOffer }

function AboutTheShip(props: Props) {
  const {
    offer,
  } = props
  const { ship } = offer
  const operatorName = offer.cruiseLine.name

  const showModal = useContext(ModalContext)

  const shipFacts = useMemo(() => {
    const facts = ship.shipInfo?.find(info => info.dataTypeName === 'Facts')
    return facts?.description ?? ''
  }, [ship.shipInfo])

  const shipFacilities = useMemo(() => {
    const facilities = ship.shipInfo?.filter(info => info.dataTypeName === 'General') ?? []
    return facilities.map(({ title, description }) => ({ title, description }))
  }, [ship.shipInfo])

  const shipImage = ship.images.length > 0 ? ship.images[0] : null

  const openShipInfoModal = useCallback(() => {
    showModal(<ShipFacilitiesModal shipFacilities={shipFacilities} />)
  }, [showModal, shipFacilities])

  return <Group direction="horizontal" horizontalAlign="stretch">
    <Group direction="vertical" gap={20}>
      <CSSBreakpoint max="tablet">
        <Group direction="horizontal" horizontalAlign="end">
          <ShipImage
            id={shipImage?.id}
            quality="best"
            alt={ship.name}
          />
        </Group>
      </CSSBreakpoint>

      <Group direction="vertical" gap={8}>
        <Heading as="h4" variant="heading3">
          {ship.name}
        </Heading>
        <BodyText variant="medium"><span>{operatorName}</span></BodyText>
      </Group>

      <Group direction="vertical" gap={16} horizontalAlign="start">
        <HTMLRenderer
          html={shipFacts}
          components={{
            ul: ({ children }) => <BodyText variant="medium" weight="normal">{children}</BodyText>,
            li: ({ children }) => <FactText variant="medium" weight="bold">{children}</FactText>,
            b: ({ children }) => <BodyText variant="medium" weight="normal">{children}</BodyText>,
          }}
        />

        {shipFacilities.length > 0 && <TextButton
          size="large"
          kind="secondary"
          onClick={openShipInfoModal}
        >
          View Facilities
        </TextButton>}
      </Group>
    </Group>

    <CSSBreakpoint min="desktop">
      <Group direction="horizontal" horizontalAlign="end">
        <ShipImage
          id={shipImage?.id}
          quality="best"
          alt={ship.name}
        />
      </Group>
    </CSSBreakpoint>
  </Group>
}

export default AboutTheShip
